import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from 'components/layouts/theme';
import Yup from 'utils/validation/yup-extensions';
import JobCategoryField from './components/JobCategoryField';
import { EPIC_LINK_COMPONENT_ID, LINK_FIELD_KEYS } from './_const';
import { isValidEmail } from 'utils';
import StateField from './components/StateField';
import HelpBar, { Main } from '../1616BridgesEpic/components/HelpBar';
import AppWrappedTextbox from '../AppWrappedTextbox';
import AlertMonitor from '../1616BridgesEpic/components/AlertMonitor';
import FormSection from '../FormSection';
import HybridContainer from '../1616BridgesEpic/components/HybridContainer';
import AppWrappedSelect from '../AppWrappedSelect';
import { fetchNpiDataByTN, fetchProviderJobCategories } from './_api';
import NPINumberField from './components/NPINumberField';
import PersonalEmailAddressField from './components/PersonalEmailAddressField';
import LocationField from './components/LocationField';
import SupervisingPhysicianField from './components/SupervisingPhysicianField';
import { MESSAGE_BANNER_TYPES } from '_types';

const SUPERVISING_PHYSICIAN_JOB_CATEGORIES = [
	'Link Physician Assistant',
	'Link Nurse Practitioner',
];

const isSupervisingPhysicianRequired = jobCategoryName =>
	SUPERVISING_PHYSICIAN_JOB_CATEGORIES.includes(jobCategoryName);

const FormView = props => {
	const isMultiUserRequest = useSelector(state =>
		state?.apps?.trackingNumber?.toLowerCase().includes('multiuser'),
	);
	const [sProviderJobCategories, setProviderJobCategories] = useState([]);

	useEffect(() => {
		let active = true;

		async function fetchApi() {
			//fetch the base job category info
			const response = await fetchProviderJobCategories();
			const data = Object.values(response).map(jc => jc.jobCategoryId);
			//set the state
			setProviderJobCategories(data);
		}

		if (active) {
			fetchApi();
		}
		if (active) {
			setProviderJobCategories([]);
		}

		return () => {
			active = false;
		};
	}, []);

	const hasSubmissionBeenAttempted = useSelector(
		state => state.apps.submissionAttempted,
	);

	const trackingNumber = useSelector(state => state.apps.trackingNumber);

	const previousSessionData = useSelector(state => {
		const formIndex = Object.keys(state.apps.allFormsInitial).find(
			formIndex =>
				state?.apps?.allFormsInitial[formIndex]?.id === props.appId,
		);
		const memberKey = formIndex
			? Object.keys(
					state?.apps?.allFormsInitial[formIndex]?.model?.members,
				).find(
					k =>
						state.apps.allFormsInitial[formIndex].model.members[k]
							.id === EPIC_LINK_COMPONENT_ID,
				)
			: null;
		const memberValue = memberKey
			? state.apps.allFormsInitial[formIndex].model.members[memberKey]
					.value
			: null;
		if (memberValue) {
			try {
				const obj = JSON.parse(memberValue);
				return obj;
			} catch {
				return null;
			}
		} else {
			return null;
		}
	});

	const validationSchema = Yup.object().shape({
		[LINK_FIELD_KEYS.PHONE_NUMBER]: Yup.string(),
		[LINK_FIELD_KEYS.EMAIL_ADDRESS]: Yup.string()
			.maxCounter(255)
			.required('Email Address is required')
			.test('isValidEmail', async function (value) {
				if (value) {
					return (
						isValidEmail(value) ||
						this.createError({
							message: 'Invalid Email Address Format',
						})
					);
				} else {
					return true;
				}
			}),
		[LINK_FIELD_KEYS.JOB_CATEGORY]: Yup.object()
			.shape({
				jobCategoryId: Yup.number().required(),
				jobCategoryName: Yup.string().required(),
			})
			.default(undefined)
			.nullable()
			.required('Please select a job category'),
		[LINK_FIELD_KEYS.SURGICAL_SCHEDULING]: Yup.string().required(
			'Please indicate if the user will be scheduling surgical cases.',
		),
		[LINK_FIELD_KEYS.NPI]: Yup.string()
			.maxCounter(10)
			.test('isProviderNpi', async function (value) {
				//is it rendered?
				if (
					this.parent[LINK_FIELD_KEYS.JOB_CATEGORY]?.jobCategoryId &&
					sProviderJobCategories.includes(
						this.parent[LINK_FIELD_KEYS.JOB_CATEGORY].jobCategoryId,
					)
				) {
					if (value) {
						//check if it is a number
						if (isNaN(value) || isNaN(parseFloat(value))) {
							return this.createError({
								message: 'NPI must contain only numbers.',
							});
						}

						//check against NPI Registry
						if (value.length === 10) {
							const { messages } = await fetchNpiDataByTN(
								value,
								trackingNumber,
							);
							if (
								messages?.length > 0 &&
								messages[0]?.Key === MESSAGE_BANNER_TYPES.ERROR
							) {
								return this.createError({
									message: `${messages[0].Value} Please try again.`,
								});
							}
							if (
								messages?.length > 0 &&
								messages[0]?.Key ===
									MESSAGE_BANNER_TYPES.WARNING
							) {
								return this.createError({
									message: `${messages[0].Value} Please search another number.`,
								});
							}

							return true;
						} else {
							return this.createError({
								message: 'An NPI number must be 10 digits.',
							});
						}
					} else {
						return this.createError({
							message: 'NPI is required',
						});
					}
				} else {
					return true;
				}
			}),
		[LINK_FIELD_KEYS.NPI_VERIFICATION]: Yup.bool().when(
			LINK_FIELD_KEYS.JOB_CATEGORY,
			{
				is: value =>
					value?.jobCategoryId &&
					sProviderJobCategories.includes(value?.jobCategoryId),
				then: schema =>
					schema
						.oneOf([true], 'Please acknowledge.')
						.required('Please acknowledge.'),
			},
		),
		[LINK_FIELD_KEYS.LICENSE_ID]: Yup.string()
			.maxCounter(255)
			.test('isProviderLicenseID', async function (value) {
				//is it rendered?
				if (
					this.parent[LINK_FIELD_KEYS.JOB_CATEGORY]?.jobCategoryId &&
					sProviderJobCategories.includes(
						this.parent[LINK_FIELD_KEYS.JOB_CATEGORY].jobCategoryId,
					)
				) {
					if (value) {
						return true;
					} else {
						return this.createError({
							message: 'License ID is required',
						});
					}
				} else {
					return true;
				}
			}),
		[LINK_FIELD_KEYS.LICENSE_STATE]: Yup.string()
			.maxCounter(255)
			.test('isProviderLicenseState', async function (value) {
				//is it rendered?
				if (
					this.parent[LINK_FIELD_KEYS.JOB_CATEGORY]?.jobCategoryId &&
					sProviderJobCategories.includes(
						this.parent[LINK_FIELD_KEYS.JOB_CATEGORY].jobCategoryId,
					)
				) {
					if (value) {
						return true;
					} else {
						return this.createError({
							message: 'License State is required',
						});
					}
				} else {
					return true;
				}
			}),
		[LINK_FIELD_KEYS.SUPERVISING_PHYSICIAN]: Yup.object()
			.shape({
				firstName: Yup.string(),
				lastName: Yup.string(),
				middleName: Yup.string(),
				npi: Yup.string(),
				credential: Yup.string(),
				address: Yup.string(),
				telephone: Yup.string(),
			})
			.default(undefined)
			.nullable()
			.test('isPhysicianSupervisingPhysician', function (value) {
				//is it rendered?
				if (
					isSupervisingPhysicianRequired(
						this.parent[LINK_FIELD_KEYS.JOB_CATEGORY]
							?.jobCategoryName,
					)
				) {
					if (value) {
						return true;
					} else {
						return this.createError({
							message: 'Supervising Physician is required',
						});
					}
				} else {
					return true;
				}
			}),
		[LINK_FIELD_KEYS.LOCATIONS]: Yup.array()
			.of(Yup.string())
			.min(1, 'Please select a site'),
		[LINK_FIELD_KEYS.COMMENTS]: Yup.string().maxCounter(256),
	});

	const defaultValues = {
		[LINK_FIELD_KEYS.PHONE_NUMBER]: '',
		[LINK_FIELD_KEYS.EMAIL_ADDRESS]: '',
		[LINK_FIELD_KEYS.JOB_CATEGORY]: '',
		[LINK_FIELD_KEYS.SURGICAL_SCHEDULING]: '',
		[LINK_FIELD_KEYS.NPI]: '',
		[LINK_FIELD_KEYS.NPI_VERIFICATION]: false,
		[LINK_FIELD_KEYS.LICENSE_ID]: '',
		[LINK_FIELD_KEYS.LICENSE_STATE]: '',
		[LINK_FIELD_KEYS.SUPERVISING_PHYSICIAN]: null,
		[LINK_FIELD_KEYS.LOCATIONS]: [],
		[LINK_FIELD_KEYS.COMMENTS]: '',
	};

	return (
		<ThemeProvider theme={theme}>
			<Box>
				<CssBaseline />
				<Main open={false}>
					<Formik
						initialValues={{
							...defaultValues,
							...previousSessionData,
						}}
						validationSchema={validationSchema}
						validateOnMount
					>
						{formik => (
							<HybridContainer
								formik={formik}
								hasSubmissionBeenAttempted={
									hasSubmissionBeenAttempted
								}
								fieldKeys={LINK_FIELD_KEYS}
								{...props}
							>
								<Box
									sx={{
										display: 'flex',
										flexWrap: 'wrap',
										flexDirection: 'row',
									}}
								>
									<AlertMonitor formik={formik} />
									<FormSection
										id={'primary-section'}
										name={'EPICCARE_LINK.SECTION_MAIN'}
										fullWidth
										defaultOpen
									>
										<PersonalEmailAddressField
											name={LINK_FIELD_KEYS.EMAIL_ADDRESS}
											initialValue={
												previousSessionData?.[
													LINK_FIELD_KEYS
														.EMAIL_ADDRESS
												]
											}
											formik={formik}
										/>
										<JobCategoryField
											name={LINK_FIELD_KEYS.JOB_CATEGORY}
											isRequired
											customFilter={jc =>
												isMultiUserRequest
													? !sProviderJobCategories.includes(
															jc.value
																.jobCategoryId,
														)
													: true
											}
											formik={formik}
										/>

										<AppWrappedSelect
											name={
												LINK_FIELD_KEYS.SURGICAL_SCHEDULING
											}
											label='Will this person be scheduling surgical cases?'
											formik={formik}
											isRequired
											options={[
												{
													key: `${LINK_FIELD_KEYS.SURGICAL_SCHEDULING}-YES`,
													value: 'Yes',
												},
												{
													key: `${LINK_FIELD_KEYS.SURGICAL_SCHEDULING}-NO`,
													value: 'No',
												},
											]}
										/>
										{sProviderJobCategories.includes(
											formik.values[
												LINK_FIELD_KEYS.JOB_CATEGORY
											]?.jobCategoryId,
										) && (
											<>
												<NPINumberField
													id={LINK_FIELD_KEYS.NPI}
													idVerification={
														LINK_FIELD_KEYS.NPI_VERIFICATION
													}
													formik={formik}
													trackingNumber={
														trackingNumber
													}
												/>
												<AppWrappedTextbox
													name={
														LINK_FIELD_KEYS.LICENSE_ID
													}
													isRequired
													label='License ID'
													formik={formik}
													helperText='License ID is required based on the job category you selected.'
												/>
												<StateField formik={formik} />
											</>
										)}
										{isSupervisingPhysicianRequired(
											formik.values[LINK_FIELD_KEYS.JOB_CATEGORY]
												?.jobCategoryName,
										) && (
												<SupervisingPhysicianField
													name={
														LINK_FIELD_KEYS.SUPERVISING_PHYSICIAN
													}
													label={`Please identify the default supervising physician for this ${formik.values[
														LINK_FIELD_KEYS
															.JOB_CATEGORY
													]?.jobCategoryName?.replace(
														/^Link\s*/,
														'',
													)}`}
													formik={formik}
													helperText={
														<>
															Search by first name, last name, and/or NPI, then select the correct physician. We gather this information to expedite billing due to Pennsylvania payer claim requirements. For additional search capabilities, you can visit {' '}
															<a
																href='https://npiregistry.cms.hhs.gov/search'
																target='_blank'
																rel='noopener noreferrer'
															>
																NPI Registry
															</a>
															.
														</>
													}
												/>
											)}
										<LocationField
											isRequired
											formik={formik}
										/>
										<AppWrappedTextbox
											name={LINK_FIELD_KEYS.COMMENTS}
											label='Comments'
											formik={formik}
											multiline
											rows={2}
										/>
									</FormSection>
								</Box>
							</HybridContainer>
						)}
					</Formik>
				</Main>
				<HelpBar open={false}></HelpBar>
			</Box>
		</ThemeProvider>
	);
};

export default FormView;
